import { Alert, Button, Card, Form, Input, InputNumber, Select, Space, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import { EducationSessionSelect } from '@/pages/education/components/EducationLookupSelects';

type Period = {
  id: string;
  session_id: string;
  code: string;
  name: string;
  sequence_no: number;
  start_time?: string | null;
  end_time?: string | null;
  is_break?: number | boolean;
};

type FormValues = {
  session_id: string;
  code: string;
  name: string;
  sequence_no: number;
  start_time?: string;
  end_time?: string;
  is_break: boolean;
};

export default function TimetablePeriodFormPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.timetable.manage') || user?.permissions?.includes('education.admin')
  );
  const isNew = !id || id === 'new';
  const [form] = Form.useForm<FormValues>();

  const list = useQuery({
    queryKey: ['education-timetable-periods-all'],
    enabled: !isNew && Boolean(id),
    queryFn: async () => {
      const res = await api.get<Period[]>('/education/timetable/periods');
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  useEffect(() => {
    if (isNew) {
      form.setFieldsValue({
        session_id: searchParams.get('session_id') || undefined,
        sequence_no: 1,
        is_break: false,
      });
      return;
    }
    const row = (list.data ?? []).find((p) => p.id === id);
    if (!row) return;
    form.setFieldsValue({
      session_id: row.session_id,
      code: row.code,
      name: row.name,
      sequence_no: row.sequence_no,
      start_time: row.start_time || undefined,
      end_time: row.end_time || undefined,
      is_break: row.is_break === 1 || row.is_break === true,
    });
  }, [form, id, isNew, list.data, searchParams]);

  const save = useMutation({
    mutationFn: (values: FormValues) =>
      isNew
        ? api.post('/education/timetable/periods', values)
        : api.put(`/education/timetable/periods/${id}`, values),
    onSuccess: () => {
      message.success(isNew ? t('periodCreated') : t('periodUpdated'));
      queryClient.invalidateQueries({ queryKey: ['education-timetable-periods'] });
      navigate('/education/timetable/periods');
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  if (!canManage) {
    return (
      <EducationPageChrome title={isNew ? t('newPeriod') : t('editPeriod')}>
        <Alert type="warning" showIcon message={t('unavailable', { status: 403 })} />
      </EducationPageChrome>
    );
  }

  return (
    <EducationPageChrome
      title={isNew ? t('newPeriod') : t('editPeriod')}
      description={t('timetablePeriodsIntro')}
      extra={<Button onClick={() => navigate('/education/timetable/periods')}>{t('backToPeriods')}</Button>}
    >
      <Card size="small" style={{ maxWidth: 640 }}>
        <Form form={form} layout="vertical" onFinish={(values) => save.mutate(values)}>
          <Form.Item name="session_id" label={t('session')} rules={[{ required: true }]}>
            <EducationSessionSelect style={{ width: '100%' }} disabled={!isNew} />
          </Form.Item>
          <Form.Item name="sequence_no" label={t('sequence')} rules={[{ required: true }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="code" label={t('code')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="name" label={t('name')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="start_time" label={t('startTime')}>
            <Input placeholder="HH:MM:SS" />
          </Form.Item>
          <Form.Item name="end_time" label={t('endTime')}>
            <Input placeholder="HH:MM:SS" />
          </Form.Item>
          <Form.Item name="is_break" label={t('break')} rules={[{ required: true }]}>
            <Select
              options={[
                { value: false, label: t('teachingPeriod') },
                { value: true, label: t('break') },
              ]}
            />
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={save.isPending}>
              {t('save')}
            </Button>
            <Button onClick={() => navigate('/education/timetable/periods')}>{t('cancel')}</Button>
          </Space>
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
